import React, { useState } from 'react';
import { useNoteListingSearch, useLinkNoteListing } from '../hooks/useNoteRequests';
import { formatEventDateOnly, formatDateTimeEastern } from '../utils/dates';
import { HighlightToken, normalizeOrder, useDebounced } from './NoteRequestBits';

// Inline "Link listing" panel under a staff queue/waiting row: searches the
// server's inventory cache (staff-only endpoint — rows carry INTERNAL notes
// with buyer emails, never shown to buyers) and links a listing to the
// request by hand when the matcher couldn't. Prefilled with the order number;
// an inventory ID can also be typed directly.
//
// Props: request (RequestView), onLinked(request), onMessage(msg, isError)
function NoteRequestLinkPanel({ request, onLinked, onMessage }) {
  const [q, setQ] = useState(request?.orderNumber || '');
  const [invId, setInvId] = useState('');
  const debouncedQ = useDebounced(q, 300);
  const { data, isFetching, error } = useNoteListingSearch(debouncedQ);
  const link = useLinkNoteListing();

  const already = new Set((request?.listings || []).map(l => Number(l.inventoryId)));
  const needle = normalizeOrder(request?.orderKey || request?.orderNumber || '');

  const doLink = (inventoryId) => {
    const id = Number(inventoryId);
    if (!Number.isFinite(id) || id <= 0) { onMessage?.('Enter a numeric inventory ID', true); return; }
    link.mutate({ id: request.id, inventoryId: id }, {
      onSuccess: (res) => {
        onMessage?.(`Linked listing #${id} to request #${request.id}`);
        setInvId('');
        onLinked?.(res.data.request);
      },
      onError: (err) => {
        const code = err.response?.data?.code;
        onMessage?.(code === 'NOT_IN_CACHE'
          ? `Listing #${id} is not in the inventory cache (sold, wrong ID, or the cache hasn't refreshed yet)`
          : err.response?.data?.error || 'Failed to link listing', true);
      },
    });
  };

  const rows = data?.rows || [];

  return (
    <div className="nr-link-panel">
      <div className="nr-link-controls">
        <input
          type="text"
          className="form-input"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search internal notes, event, performer, venue, section, row or listing ID…"
          autoFocus
        />
        <div className="nr-link-byid">
          <input
            type="number"
            className="form-input"
            value={invId}
            onChange={e => setInvId(e.target.value)}
            placeholder="or inventory ID"
            min="1"
            onKeyDown={e => { if (e.key === 'Enter') doLink(invId); }}
          />
          <button className="btn btn-primary btn-sm" onClick={() => doLink(invId)} disabled={link.isPending || !invId}>Link ID</button>
        </div>
      </div>

      {data && data.cacheLoaded === false && (
        <div className="ai-stale-banner" style={{ marginTop: 8 }}>Inventory cache is still loading on the server — search will work once the first full refresh completes.</div>
      )}
      {error && <div className="ai-inline-error" style={{ marginTop: 8 }}>{error.response?.data?.error || 'Search failed'}</div>}

      {debouncedQ.trim().length < 2 ? (
        <div className="nr-muted" style={{ marginTop: 8 }}>Type at least 2 characters to search.</div>
      ) : (
        <div className="table-container" style={{ marginTop: 8 }}>
          <table className="nr-search-table">
            <thead>
              <tr>
                <th>ID</th><th>Event</th><th>Date</th><th>Sec</th><th>Row</th><th>Seats</th><th>Qty</th>
                <th>Internal notes</th><th>Public notes</th><th>Created</th><th>Requested?</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="12" className="nr-muted" style={{ textAlign: 'center' }}>{isFetching ? 'Searching…' : 'No listings match'}</td></tr>
              ) : rows.map(r => {
                const linked = already.has(Number(r.id));
                return (
                  <tr key={r.id} className={linked ? 'nr-row-linked' : ''}>
                    <td className="nr-mono">{r.id}</td>
                    <td style={{ maxWidth: 220 }}>
                      {r.eventName}
                      {r.performer && r.performer !== r.eventName ? <div className="nr-muted" style={{ fontSize: 11 }}>{r.performer}</div> : null}
                      {r.venue ? <div className="nr-muted" style={{ fontSize: 11 }}>{r.venue}</div> : null}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatEventDateOnly(r.eventDate)}</td>
                    <td>{r.section}</td>
                    <td>{r.row}</td>
                    <td>{r.seats}</td>
                    <td>{r.quantity}</td>
                    <td className="nr-notes-cell nr-evidence"><HighlightToken text={r.notes} needle={needle} /></td>
                    <td className="nr-notes-cell">{r.publicNotes || <em className="nr-muted">—</em>}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{formatDateTimeEastern(r.createdDate)}{r.createdBy ? <div className="nr-muted">{r.createdBy}</div> : null}</td>
                    <td>{r.requested ? <span className="nr-chip nr-state-pending">yes</span> : <span className="nr-muted">—</span>}</td>
                    <td>
                      {linked ? (
                        <span className="nr-muted">linked</span>
                      ) : (
                        <button className="btn btn-primary btn-sm" onClick={() => doLink(r.id)} disabled={link.isPending} title={r.status && r.status !== 'AVAILABLE' ? `Listing status is ${r.status} — the writer will skip it until it is AVAILABLE` : 'Link this listing to the request'}>
                          Link{r.status && r.status !== 'AVAILABLE' ? ` (${r.status})` : ''}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default NoteRequestLinkPanel;
